import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { contentApi, formatApiError } from '../lib/api';
import { progressService } from '../services/progress.service';
import { Button } from '../components/ui/button';
import {
  Play, LogOut, User, Share2, Radio, Clock,
  CheckCircle, Lock, CalendarClock, Video, ChevronRight, X,
} from 'lucide-react';

const TOTAL_DAYS = 21;
const SITE_URL = 'https://thekalarimovement.com';

function shareText(text) {
  if (navigator.share) {
    navigator.share({ text }).catch(() => {});
  } else {
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank', 'noopener,noreferrer');
  }
}

function normalizeSession(s) {
  return {
    ...s,
    join_url: s.can_join ? contentApi.getJoinUrl(s.day) : null,
    replay_url: s.is_replay_available ? contentApi.getReplayUrl(s.day) : null,
  };
}

// Calendar-based: a day is accessible if its live session date has passed or is today.
// No sequential locking — Day 4 doesn't require Days 1-3 to be done.
function computeDayState(session, dayProgress) {
  const now = Date.now();

  if (!session) return 'unscheduled';
  if (session.is_live_now) return 'live';

  const startMs = session.live_start_at ? new Date(session.live_start_at).getTime() : null;
  const hasStarted = startMs && startMs <= now;

  if (dayProgress?.completed) {
    return session.is_replay_available ? 'completed-replay' : 'completed';
  }
  if (session.is_replay_available) return 'replay';
  if (!hasStarted) return 'upcoming';
  return 'ended'; // session passed, no replay, not completed
}

function useCountdownMs(targetIso) {
  const [ms, setMs] = useState(() => targetIso ? new Date(targetIso).getTime() - Date.now() : 0);
  useEffect(() => {
    if (!targetIso) return;
    const id = setInterval(() => setMs(new Date(targetIso).getTime() - Date.now()), 1000);
    return () => clearInterval(id);
  }, [targetIso]);
  return Math.max(0, ms);
}

function formatMs(ms) {
  if (ms <= 0) return null;
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  if (h >= 24) return `${Math.floor(h / 24)}d ${h % 24}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

export default function UserDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [dayProgress, setDayProgress] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [playingSession, setPlayingSession] = useState(null);

  const handleWatch = useCallback(async (session) => {
    try {
      const res = await contentApi.fetchReplayUrl(session.day);
      setPlayingSession({ ...session, replay_url: res.data.url });
    } catch {
      setPlayingSession(session);
    }
  }, []);

  const fetchAll = useCallback(async () => {
    try {
      const fetches = [
        contentApi.getLiveSessions().catch(() => ({ data: [] })),
        contentApi.getProgrammeSettings().catch(() => ({ data: {} })),
      ];
      if (user?.is_registered) {
        fetches.push(progressService.getDays().catch(() => ({ data: [] })));
      }
      const [sessionsRes, settingsRes, progressRes] = await Promise.all(fetches);
      setSessions(sessionsRes?.data || []);
      setSettings(settingsRes?.data || null);
      setDayProgress(progressRes?.data || []);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (!user) { navigate('/login?redirect=/dashboard'); return; }
    fetchAll();
    const id = setInterval(fetchAll, 30_000);
    return () => clearInterval(id);
  }, [user, navigate, fetchAll]);

  const markDone = async (day) => {
    try {
      await progressService.updateLesson(`day-${day}`, { completed: true, progress_percent: 100 });
      setDayProgress(prev => prev.map(p => p.day === day ? { ...p, completed: true, progress_percent: 100 } : p));
    } catch {
      alert('Could not save progress. Please check your connection and try again.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#9A3B26] border-t-transparent" />
      </div>
    );
  }

  const sessionByDay = Object.fromEntries(sessions.map(s => [s.day, s]));
  const progressByDay = Object.fromEntries((dayProgress || []).map(p => [p.day, p]));

  const completedDays = (dayProgress || []).filter(p => p.completed).length;
  const progressPercent = Math.round((completedDays / TOTAL_DAYS) * 100);

  // Today's most relevant session (live first, then today's, then next upcoming)
  const now = new Date();
  const todayStr = now.toISOString().slice(0, 10);
  const highlighted =
    sessions.find(s => s.is_live_now) ||
    sessions.find(s => s.live_start_at && new Date(s.live_start_at).toISOString().slice(0, 10) === todayStr) ||
    [...sessions]
      .filter(s => s.live_start_at && new Date(s.live_start_at) > now)
      .sort((a, b) => new Date(a.live_start_at) - new Date(b.live_start_at))[0] ||
    null;

  // Sessions with active replay, excluding today's highlighted card
  const catchUpSessions = sessions.filter(
    s => s.is_replay_available && s.day !== highlighted?.day
  ).sort((a, b) => (a.day || 0) - (b.day || 0));

  // Next upcoming session — the soonest future session that isn't the highlighted one
  const nextUpcoming = [...sessions]
    .filter(s => s.live_start_at && new Date(s.live_start_at) > now && s.day !== highlighted?.day)
    .sort((a, b) => new Date(a.live_start_at) - new Date(b.live_start_at))[0] || null;

  const weeks = [
    { label: 'Week 1', days: [1, 2, 3, 4, 5, 6, 7] },
    { label: 'Week 2', days: [8, 9, 10, 11, 12, 13, 14] },
    { label: 'Week 3', days: [15, 16, 17, 18, 19, 20, 21] },
  ];

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {playingSession && (
        <VideoPlayerModal
          session={playingSession}
          user={user}
          onClose={() => setPlayingSession(null)}
        />
      )}
      <header className="bg-white border-b border-[#E7E5E4] sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="font-heading text-xl font-bold text-[#9A3B26]">Kalaripayattu</Link>
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm"
              onClick={() => shareText(`I'm on Day ${completedDays + 1} of my 21-Day Kalaripayattu journey! 🥋 Join me: ${SITE_URL}`)}
              className="text-[#78716C] hover:text-[#9A3B26] hidden sm:inline-flex gap-1.5">
              <Share2 className="h-4 w-4" /> Invite a friend
            </Button>
            <div className="w-9 h-9 bg-[#9A3B26] rounded-full flex items-center justify-center">
              <User className="h-4 w-4 text-white" />
            </div>
            <Button data-testid="logout-btn" variant="ghost" size="icon"
              onClick={async () => { await logout(); navigate('/'); }}
              className="text-[#78716C] hover:text-[#9A3B26]">
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-6">

        {/* Hero */}
        <DashboardHero user={user} completedDays={completedDays} progressPercent={progressPercent} />

        {/* Pre-programme countdown + prep checklist */}
        <ProgrammeCountdown startDateIso={settings?.start_date} />
        <WhatToPrepare startDateIso={settings?.start_date} />

        {/* Unregistered gate */}
        {!user?.is_registered && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
            <h2 className="font-heading text-lg font-bold text-amber-900 mb-1">Sessions locked</h2>
            <p className="text-amber-800 text-sm mb-3">Your account is active but sessions require registration.</p>
            <Button onClick={() => navigate('/#register-section')} className="bg-[#9A3B26] hover:bg-[#7A2E1E] text-white text-sm">
              Contact us to register
            </Button>
          </div>
        )}

        {/* Today's / next session highlight */}
        {highlighted && (
          <HighlightedSession
            session={normalizeSession(highlighted)}
            dayProgress={progressByDay[highlighted.day]}
            onMarkDone={() => markDone(highlighted.day)}
            onWatch={handleWatch}
          />
        )}

        {/* Catch-up replays */}
        {catchUpSessions.length > 0 && (
          <div className="space-y-2">
            <h3 className="font-heading text-sm font-semibold text-[#78716C] uppercase tracking-widest">
              Catch up — recording expiring soon
            </h3>
            {catchUpSessions.map(s => (
              <ReplayBanner
                key={s.day}
                session={normalizeSession(s)}
                dayProgress={progressByDay[s.day]}
                onMarkDone={() => markDone(s.day)}
                onWatch={handleWatch}
              />
            ))}
          </div>
        )}

        {/* Next session notice — shown when highlighted is today's completed/replay session */}
        {nextUpcoming && (
          <NextSessionNotice session={nextUpcoming} />
        )}

        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">{error}</div>
        )}

        {/* 21-day programme schedule */}
        {user?.is_registered && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-xl font-bold text-[#292524]">Programme Schedule</h2>
              <span className="text-sm text-[#78716C]">{completedDays} / {TOTAL_DAYS} completed</span>
            </div>

            {weeks.map(week => (
              <div key={week.label}>
                <p className="text-xs font-semibold uppercase tracking-widest text-[#78716C] mb-3">{week.label}</p>
                <div className="space-y-2">
                  {week.days.map(day => (
                    <DayCard
                      key={day}
                      day={day}
                      session={sessionByDay[day] ? normalizeSession(sessionByDay[day]) : undefined}
                      dayProgress={progressByDay[day]}
                      onMarkDone={() => markDone(day)}
                      onWatch={handleWatch}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

// ── Hero ─────────────────────────────────────────────────────────────────────

function DashboardHero({ user, completedDays, progressPercent }) {
  const currentDay = Math.min(completedDays + 1, TOTAL_DAYS);

  return (
    <section className="relative overflow-hidden rounded-2xl">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_18%_18%,rgba(221,161,94,0.22),transparent_44%),linear-gradient(150deg,#1E1713_0%,#2A201A_50%,#3B2A20_100%)]" />
      <div className="absolute -top-16 -right-12 h-56 w-56 rounded-full bg-[#DDA15E]/20 blur-3xl" />
      <div className="absolute -bottom-16 -left-8 h-56 w-56 rounded-full bg-[#9A3B26]/20 blur-3xl" />

      <div className="relative px-5 py-7 sm:px-8 sm:py-9 text-[#F8F3EA]">
        <p className="text-[11px] uppercase tracking-[0.24em] text-[#E8D8BE]/70 mb-2">
          Kalari Sadhana · 21-Day Journey
        </p>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold">
          Day {currentDay} <span className="text-[#DDA15E]">of {TOTAL_DAYS}</span>
        </h1>
        <p className="mt-2 text-sm text-[#F5EBDD]/80 max-w-md">
          Welcome back, {user?.name?.split(' ')[0]}. Ground your breath, awaken your stance.
        </p>

        <div className="mt-5 max-w-xs">
          <div className="flex justify-between text-xs text-[#E8D8BE]/70 mb-1.5">
            <span>{completedDays} of {TOTAL_DAYS} sessions done</span>
            <span>{progressPercent}%</span>
          </div>
          <div className="h-1.5 rounded-full bg-[#E8D8BE]/20 overflow-hidden">
            <div
              className="h-full rounded-full bg-gradient-to-r from-[#9A3B26] via-[#DDA15E] to-[#F5EBDD] transition-all duration-700"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Next session notice ───────────────────────────────────────────────────────

function NextSessionNotice({ session }) {
  const startMs = useCountdownMs(session.live_start_at);

  const sessionDate = session.live_start_at
    ? new Date(session.live_start_at).toLocaleDateString('en-IN', {
        weekday: 'short', day: 'numeric', month: 'short', timeZone: 'Asia/Kolkata',
      })
    : null;
  const sessionTime = session.live_start_at
    ? new Date(session.live_start_at).toLocaleTimeString('en-IN', {
        hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata',
      })
    : null;
  const joinTime = session.live_start_at
    ? new Date(new Date(session.live_start_at).getTime() - 5 * 60 * 1000).toLocaleTimeString('en-IN', {
        hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata',
      })
    : null;

  return (
    <div className="rounded-2xl border border-[#DDA15E]/30 bg-[#FBF6EE] px-5 py-4">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-xs font-semibold text-[#DDA15E] uppercase tracking-widest mb-1 flex items-center gap-1.5">
            <CalendarClock className="h-3.5 w-3.5" /> Next Session
          </p>
          <p className="font-heading text-base font-bold text-[#292524]">
            Day {session.day} — {session.title || `Session ${session.day}`}
          </p>
          {sessionDate && sessionTime && (
            <p className="text-sm text-[#78716C] mt-0.5">
              {sessionDate} · <span className="font-semibold text-[#44403C]">{sessionTime} IST</span>
            </p>
          )}
        </div>
        {startMs > 0 && (
          <span className="shrink-0 text-xs text-[#78716C] tabular-nums pt-1">
            in {formatMs(startMs)}
          </span>
        )}
      </div>
      <div className="mt-3 pt-3 border-t border-[#DDA15E]/20 flex items-start gap-2.5">
        <Video className="h-4 w-4 text-[#DDA15E] shrink-0 mt-0.5" />
        <p className="text-sm text-[#78716C] leading-relaxed">
          This session will be held on Zoom. Your join link will appear here automatically
          {joinTime
            ? <> from <span className="font-semibold text-[#44403C]">{joinTime} IST</span> — 5 minutes before the session starts.</>
            : <> 5 minutes before the session starts.</>
          }{' '}
          Please return to this page at that time.
        </p>
      </div>
    </div>
  );
}

// ── Today's highlighted session ───────────────────────────────────────────────

function HighlightedSession({ session, dayProgress, onMarkDone, onWatch }) {
  const state = computeDayState(session, dayProgress);
  const replayMs = useCountdownMs(session.replay_expires_at);
  const startMs = useCountdownMs(session.live_start_at);
  const isToday = session.live_start_at &&
    new Date(session.live_start_at).toISOString().slice(0, 10) === new Date().toISOString().slice(0, 10);

  return (
    <div className={`rounded-2xl border overflow-hidden ${
      state === 'live'
        ? 'border-red-400/40 bg-gradient-to-br from-[#1F0E0A] to-[#2A1410]'
        : state === 'replay' || state === 'completed-replay'
        ? 'border-[#DDA15E]/30 bg-gradient-to-br from-[#1C160E] to-[#251F13]'
        : 'border-[#E7E5E4] bg-white'
    }`}>
      <div className="px-5 py-5 sm:px-6">
        {/* Status row */}
        <div className="flex items-center gap-2 mb-3">
          {state === 'live' && (
            <span className="flex items-center gap-1.5 text-xs font-semibold text-red-400 uppercase tracking-widest">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" />
              </span>
              Live Now
            </span>
          )}
          {(state === 'replay' || state === 'completed-replay') && (
            <span className="flex items-center gap-1 text-xs font-semibold text-[#DDA15E] uppercase tracking-widest">
              <Video className="h-3.5 w-3.5" />
              Recording Available
            </span>
          )}
          {(state === 'upcoming') && (
            <span className="flex items-center gap-1 text-xs font-semibold text-[#78716C] uppercase tracking-widest">
              <CalendarClock className="h-3.5 w-3.5" />
              {isToday ? 'Today' : 'Upcoming'}
            </span>
          )}
        </div>

        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-xs text-[#78716C] mb-0.5">Day {session.day}</p>
            <h2 className={`font-heading text-lg sm:text-xl font-bold truncate ${
              state === 'live' || state === 'replay' || state === 'completed-replay'
                ? 'text-[#F8F2E8]' : 'text-[#292524]'
            }`}>
              {session.title || `Session ${session.day}`}
            </h2>
            {session.live_start_at && (
              <p className={`text-xs mt-1 ${
                state === 'live' || state === 'replay' || state === 'completed-replay'
                  ? 'text-[#E8D8BE]/60' : 'text-[#78716C]'
              }`}>
                {new Date(session.live_start_at).toLocaleDateString('en-IN', {
                  weekday: 'short', day: 'numeric', month: 'short', timeZone: 'Asia/Kolkata',
                })} · {new Date(session.live_start_at).toLocaleTimeString('en-IN', {
                  hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata',
                })} IST
              </p>
            )}
          </div>

          {/* Action button */}
          <div className="shrink-0 flex flex-col items-end gap-2">
            {state === 'live' && session.join_url && (
              <a href={session.join_url} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#DDA15E] hover:bg-[#E4B377] text-[#1F1A17] text-sm font-bold rounded-xl transition-colors shadow-lg">
                <Radio className="h-4 w-4" /> Join Live
              </a>
            )}
            {(state === 'replay' || state === 'completed-replay') && session.replay_url && (
              <button onClick={() => onWatch(session)}
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#DDA15E]/20 hover:bg-[#DDA15E]/30 text-[#DDA15E] text-sm font-semibold rounded-xl border border-[#DDA15E]/30 transition-colors">
                <Play className="h-4 w-4" /> Watch Recording
              </button>
            )}
            {state === 'upcoming' && startMs > 0 && (
              <span className="text-xs text-[#78716C] tabular-nums">
                in {formatMs(startMs)}
              </span>
            )}
          </div>
        </div>

        {/* Zoom link notice for upcoming sessions */}
        {state === 'upcoming' && (() => {
          const joinTime = session.live_start_at
            ? new Date(new Date(session.live_start_at).getTime() - 5 * 60 * 1000)
            : null;
          const joinTimeStr = joinTime
            ? joinTime.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' })
            : null;
          return (
            <div className="mt-4 pt-3 border-t border-[#E7E5E4] flex items-start gap-2.5">
              <span className="mt-0.5 shrink-0 w-4 h-4 rounded-full bg-[#F0EBE4] flex items-center justify-center">
                <span className="text-[9px]">🔗</span>
              </span>
              <p className="text-xs text-[#78716C] leading-relaxed">
                Your Zoom join link will appear here automatically
                {joinTimeStr ? <> from <span className="font-semibold text-[#44403C]">{joinTimeStr} IST</span></> : null}.
                Please return to this page a few minutes before the session.
              </p>
            </div>
          );
        })()}

        {/* Replay expiry + mark done */}
        {(state === 'replay') && (
          <div className="mt-4 flex items-center justify-between gap-3 pt-3 border-t border-[#DDA15E]/15">
            <div className="flex items-center gap-1.5 text-xs text-[#DDA15E]/80">
              <Clock className="h-3.5 w-3.5 shrink-0" />
              {replayMs > 0
                ? `Recording expires in ${formatMs(replayMs)}`
                : 'Recording expiring soon'}
            </div>
            <button onClick={onMarkDone}
              className="text-xs text-[#E8D8BE]/50 hover:text-[#DDA15E] transition-colors underline underline-offset-2">
              Mark as done
            </button>
          </div>
        )}
        {state === 'completed-replay' && (
          <div className="mt-3 flex items-center gap-1.5 text-xs text-emerald-400/80 pt-3 border-t border-emerald-400/10">
            <CheckCircle className="h-3.5 w-3.5" /> Completed
          </div>
        )}
      </div>
    </div>
  );
}

// ── Catch-up replay banner ────────────────────────────────────────────────────

function ReplayBanner({ session, dayProgress, onMarkDone, onWatch }) {
  const replayMs = useCountdownMs(session.replay_expires_at);
  const done = dayProgress?.completed;

  return (
    <div className="flex items-center justify-between gap-4 px-4 py-3 rounded-xl border border-[#DDA15E]/25 bg-[#FEF9EF]">
      <div className="min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className="text-xs font-semibold text-[#9A3B26]">Day {session.day}</span>
          {done && <CheckCircle className="h-3.5 w-3.5 text-emerald-500" />}
        </div>
        <p className="text-sm font-medium text-[#292524] truncate">{session.title}</p>
        <p className="text-xs text-[#78716C] flex items-center gap-1 mt-0.5">
          <Clock className="h-3 w-3" />
          {replayMs > 0 ? `Expires in ${formatMs(replayMs)}` : 'Expiring soon'}
        </p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {!done && (
          <button onClick={onMarkDone} className="text-xs text-[#78716C] hover:text-[#9A3B26] underline underline-offset-2">
            Mark done
          </button>
        )}
        {session.replay_url && (
          <button onClick={() => onWatch(session)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#9A3B26] hover:bg-[#7A2E1E] text-white text-xs font-semibold rounded-lg transition-colors">
            <Play className="h-3.5 w-3.5" /> Watch
          </button>
        )}
      </div>
    </div>
  );
}

// ── Programme countdown ───────────────────────────────────────────────────────

function ProgrammeCountdown({ startDateIso }) {
  const ms = useCountdownMs(startDateIso);
  if (!startDateIso || ms <= 0) return null;

  const d = Math.floor(ms / 86400000);
  const h = Math.floor((ms % 86400000) / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  const s = Math.floor((ms % 60000) / 1000);

  const dateLabel = new Date(startDateIso).toLocaleDateString('en-IN', {
    day: 'numeric', month: 'long', timeZone: 'Asia/Kolkata',
  });
  const timeLabel = new Date(startDateIso).toLocaleTimeString('en-IN', {
    hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata',
  });

  return (
    <div className="rounded-2xl border border-[#DDA15E]/30 bg-gradient-to-br from-[#1C160E] to-[#251F13] px-5 py-5 sm:px-6">
      <p className="text-[11px] uppercase tracking-[0.2em] text-[#E8D8BE]/60 mb-4">Programme begins in</p>
      <div className="flex items-end gap-5 mb-4">
        {[{ v: d, u: 'days' }, { v: h, u: 'hrs' }, { v: m, u: 'min' }, { v: s, u: 'sec' }].map(({ v, u }) => (
          <div key={u} className="text-center">
            <span className="font-heading text-3xl sm:text-4xl font-bold text-[#DDA15E] tabular-nums">
              {String(v).padStart(2, '0')}
            </span>
            <p className="text-[10px] text-[#E8D8BE]/50 uppercase tracking-widest mt-1">{u}</p>
          </div>
        ))}
      </div>
      <p className="text-xs text-[#E8D8BE]/60">{dateLabel} · {timeLabel} IST</p>
    </div>
  );
}

// ── What to prepare ───────────────────────────────────────────────────────────

function WhatToPrepare({ startDateIso }) {
  if (startDateIso && new Date(startDateIso) <= new Date()) return null;

  const items = [
    'Clear space (6×4 ft)',
    'Comfortable clothing',
    'Yoga mat (optional)',
    'Stable internet for live sessions',
  ];

  return (
    <div className="rounded-2xl border border-[#E7E5E4] bg-white px-5 py-5 sm:px-6 space-y-4">
      <h3 className="font-heading text-base font-bold text-[#292524]">What to prepare</h3>
      <ul className="space-y-3">
        {items.map(item => (
          <li key={item} className="flex items-center gap-3 text-sm text-[#44403C]">
            <span className="w-1.5 h-1.5 rounded-full bg-[#9A3B26] shrink-0" />
            {item}
          </li>
        ))}
      </ul>
      <div className="flex items-start gap-3 rounded-xl bg-[#F5F0EA] border border-[#E7E0D5] px-4 py-3">
        <Video className="h-4 w-4 text-[#9A3B26] shrink-0 mt-0.5" />
        <p className="text-xs text-[#57534E] leading-relaxed">
          All live sessions will be held on <span className="font-semibold text-[#292524]">Zoom</span>. Your join link will appear here automatically at the scheduled session time — no separate link needed.
        </p>
      </div>
    </div>
  );
}

// ── Individual day card ───────────────────────────────────────────────────────

function DayCard({ day, session, dayProgress, onMarkDone, onWatch }) {
  const state = computeDayState(session, dayProgress);
  const replayMs = useCountdownMs(session?.replay_expires_at);
  const progress = dayProgress?.progress_percent || 0;

  const dateStr = session?.live_start_at
    ? new Date(session.live_start_at).toLocaleDateString('en-IN', {
        day: 'numeric', month: 'short', timeZone: 'Asia/Kolkata',
      })
    : null;

  const statusConfig = {
    live:              { dot: 'bg-red-500 animate-pulse', label: 'Live', labelColor: 'text-red-500' },
    replay:            { dot: 'bg-[#DDA15E]', label: 'Recording', labelColor: 'text-[#DDA15E]' },
    'completed-replay':{ dot: 'bg-emerald-500', label: 'Done', labelColor: 'text-emerald-600' },
    completed:         { dot: 'bg-emerald-500', label: 'Done', labelColor: 'text-emerald-600' },
    upcoming:          { dot: 'bg-[#E7E5E4]', label: dateStr || 'Upcoming', labelColor: 'text-[#78716C]' },
    ended:             { dot: 'bg-[#D5CCC0]', label: 'Ended', labelColor: 'text-[#A8A29E]' },
    unscheduled:       { dot: 'bg-[#E7E5E4]', label: '—', labelColor: 'text-[#C0B8AE]' },
  };

  const cfg = statusConfig[state] || statusConfig.unscheduled;
  const isDark = state === 'live' || state === 'replay';
  const isAccessible = state !== 'upcoming' && state !== 'unscheduled';

  return (
    <div className={`rounded-xl border px-4 py-3 transition-all ${
      state === 'live'
        ? 'border-red-400/30 bg-gradient-to-r from-[#200D0A] to-[#2A1410]'
        : state === 'replay'
        ? 'border-[#DDA15E]/25 bg-[#FEF9EF]'
        : state === 'completed' || state === 'completed-replay'
        ? 'border-emerald-200 bg-emerald-50/50'
        : 'border-[#E7E5E4] bg-white'
    }`}>
      <div className="flex items-center gap-3">
        {/* Day number */}
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center text-sm font-bold shrink-0 ${
          state === 'completed' || state === 'completed-replay'
            ? 'bg-emerald-100 text-emerald-700'
            : state === 'live'
            ? 'bg-red-500/20 text-red-400'
            : state === 'replay'
            ? 'bg-[#DDA15E]/15 text-[#DDA15E]'
            : state === 'upcoming' || state === 'unscheduled'
            ? 'bg-[#F4EFE6] text-[#A8A29E]'
            : 'bg-[#F4EFE6] text-[#78716C]'
        }`}>
          {state === 'completed' || state === 'completed-replay'
            ? <CheckCircle className="h-4 w-4" />
            : state === 'upcoming' || state === 'unscheduled'
            ? <Lock className="h-3.5 w-3.5 opacity-50" />
            : day}
        </div>

        {/* Title + meta */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${cfg.dot}`} />
            <span className={`text-xs font-medium ${cfg.labelColor}`}>{cfg.label}</span>
            {(state === 'replay') && replayMs > 0 && (
              <span className="text-xs text-[#78716C] flex items-center gap-0.5">
                <Clock className="h-3 w-3" /> {formatMs(replayMs)}
              </span>
            )}
          </div>
          <p className={`text-sm font-semibold truncate ${isDark ? 'text-[#F8F2E8]' : 'text-[#292524]'}`}>
            {session?.title || `Day ${day} Session`}
          </p>
          {dateStr && (
            <p className={`text-xs mt-0.5 ${isDark ? 'text-[#E8D8BE]/50' : 'text-[#A8A29E]'}`}>{dateStr}</p>
          )}
          {/* Per-day progress bar (only when there's progress) */}
          {isAccessible && progress > 0 && progress < 100 && (
            <div className="mt-1.5 h-1 bg-[#E7E5E4] rounded-full overflow-hidden max-w-[120px]">
              <div className="h-full bg-[#2C5E4B] rounded-full transition-all" style={{ width: `${progress}%` }} />
            </div>
          )}
        </div>

        {/* Action */}
        <div className="shrink-0">
          {state === 'live' && session?.join_url && (
            <a href={session.join_url} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#DDA15E] hover:bg-[#E4B377] text-[#1F1A17] text-xs font-bold rounded-lg transition-colors">
              <Radio className="h-3.5 w-3.5" /> Join
            </a>
          )}
          {(state === 'replay' || state === 'completed-replay') && session?.replay_url && (
            <div className="flex flex-col items-end gap-1">
              <button onClick={() => onWatch(session)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#9A3B26]/10 hover:bg-[#9A3B26]/20 text-[#9A3B26] text-xs font-semibold rounded-lg border border-[#9A3B26]/20 transition-colors">
                <Play className="h-3.5 w-3.5" /> Watch
              </button>
              {state === 'replay' && (
                <button onClick={onMarkDone}
                  className="text-[10px] text-[#A8A29E] hover:text-[#9A3B26] underline underline-offset-2 transition-colors">
                  Mark done
                </button>
              )}
            </div>
          )}
          {(state === 'ended') && (
            <span className="text-xs text-[#C0B8AE]">Expired</span>
          )}
          {(state === 'upcoming') && session?.live_start_at && (
            <span className="text-xs text-[#A8A29E] flex items-center gap-1">
              <ChevronRight className="h-3.5 w-3.5" />
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Vimeo URL converter ───────────────────────────────────────────────────────
// Accepts a Vimeo share URL (https://vimeo.com/123456789 or with private hash)
// and returns the embed URL with no badge, no tracking, autopause off.
function toEmbedUrl(url) {
  if (!url) return url;
  if (url.includes('player.vimeo.com')) return url;
  const m = url.match(/vimeo\.com\/(\d+)(?:\/([a-zA-Z0-9]+))?/);
  if (m) {
    const params = new URLSearchParams({ badge: '0', autopause: '0', player_id: '0', app_id: '58479' });
    if (m[2]) params.set('h', m[2]);
    return `https://player.vimeo.com/video/${m[1]}?${params}`;
  }
  return url;
}

// ── Video Player Modal with Watermark ─────────────────────────────────────────

const WATERMARK_POSITIONS = [
  { top: '8%',  left: '5%'  },
  { top: '8%',  left: '55%' },
  { top: '38%', left: '25%' },
  { top: '62%', left: '5%'  },
  { top: '62%', left: '58%' },
  { top: '88%', left: '30%' },
];

function VideoPlayerModal({ session, user, onClose }) {
  const url = session.replay_url;
  const isDirectVideo = /\.(mp4|webm|ogg)(\?|$)/i.test(url);
  const isVimeo = url && url.includes('vimeo.com');
  const embedUrl = isDirectVideo ? url : toEmbedUrl(url);
  const watermarkText = `${user?.name || ''} · ${user?.email || ''}`;
  const videoRef = useRef(null);

  // Close on Escape key
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  // Prevent background scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  // Non-Vimeo URLs (e.g. Zoom recording links) can't be embedded — open in new tab
  if (url && !isVimeo && !isDirectVideo) {
    window.open(url, '_blank', 'noopener,noreferrer');
    onClose();
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/95 flex flex-col items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Header */}
      <div className="w-full max-w-4xl flex items-center justify-between mb-3">
        <div>
          <p className="text-[#DDA15E] text-xs font-semibold uppercase tracking-widest">Day {session.day}</p>
          <p className="text-white text-sm font-medium">{session.title}</p>
        </div>
        <button
          onClick={onClose}
          className="text-white/60 hover:text-white transition-colors p-1"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Player + watermark */}
      <div className="relative w-full max-w-4xl" style={{ aspectRatio: '16/9' }}>

        {/* Video */}
        {isDirectVideo ? (
          <video
            ref={videoRef}
            src={url}
            controls
            autoPlay
            controlsList="nodownload"
            onContextMenu={(e) => e.preventDefault()}
            className="w-full h-full rounded-xl bg-black"
          />
        ) : (
          <iframe
            src={embedUrl}
            className="w-full h-full rounded-xl"
            allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
            allowFullScreen
            referrerPolicy="strict-origin-when-cross-origin"
            title={session.title}
          />
        )}

        {/* Watermark overlay — pointer-events none so controls still work */}
        <div className="absolute inset-0 rounded-xl pointer-events-none select-none overflow-hidden">
          {WATERMARK_POSITIONS.map((pos, i) => (
            <div
              key={i}
              style={{
                position: 'absolute',
                top: pos.top,
                left: pos.left,
                transform: 'rotate(-25deg)',
                opacity: 0.13,
                whiteSpace: 'nowrap',
              }}
            >
              <span className="text-white font-mono text-xs tracking-wider">
                {watermarkText}
              </span>
            </div>
          ))}
        </div>
      </div>

      <p className="mt-3 text-white/25 text-[10px] font-mono tracking-wider select-none">
        {watermarkText} · thekalarimovement.com
      </p>
    </div>
  );
}
